import React, { useEffect, useRef, useState } from "react";
import "./SearchSelect.scss";
import { IconSearch } from "../../icons/IconSearch/IconSearch";
import { IconClose } from "../../icons/IconClose/IconClose";

export type SearchSelectItem = {
  key: string;
  label: string;
  sublabel?: string;
};

export type SearchCallback = (searchText: string) => SearchSelectItem[];
export type SelectionCallback = (key: string) => { displayName: string };

export type Props = {
  placeholderText: string;
  clearSearchAriaLabel: string;
  searchCallback: SearchCallback;
  selectionCallback: SelectionCallback;
  searchText: string;
  disabled?: boolean;
  clear: () => void;
};

export const SearchSelect = ({
  placeholderText,
  clearSearchAriaLabel,
  searchCallback,
  selectionCallback,
  searchText,
  disabled,
  clear,
}: Props) => {
  const itemRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([]);
  const [inputText, setInputText] = useState<string>("");
  const [mappedSearchResults, setMappedSearchResults] = useState<SearchSelectItem[]>([]);
  const selectedIndexRef = useRef(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeIconRef = useRef<HTMLDivElement>(null);
  const [selectedFullName, setSelectedFullName] = useState<string>(searchText);
  const [activeDescendantKey, setActiveDescendantKey] = useState<string>("");

  useEffect(() => {
    setSelectedFullName(searchText);
  }, [searchText]);

  // Tracks whether the current inputText reflects a completed selection rather
  // than free typing, so the effect below can skip re-querying and reopening
  // the dropdown when searchCallback's identity changes as a side effect of
  // that selection (e.g. the search pool updating after a node expands).
  const justSelectedRef = useRef(false);

  // Re-run the active query when the search pool changes (e.g. group leader selected).
  // Keeps the dropdown in sync without forcing the user to retype.
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (inputText) {
      const results = searchCallback(inputText);
      setMappedSearchResults(results);
      itemRefs.current = results.map(() => React.createRef<HTMLDivElement>());
    } else {
      setMappedSearchResults([]);
    }
  }, [searchCallback]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelection = (key: string) => {
    justSelectedRef.current = true;
    const selectionCallbackResult = selectionCallback(key);
    setSelectedFullName(selectionCallbackResult.displayName);
    // Clear the free-typed query text so a later searchCallback identity change
    // (e.g. isolate/undo-isolate refreshing the search pool) doesn't re-run the
    // effect below against stale text and reopen the dropdown unprompted.
    setInputText("");

    setMappedSearchResults([]);
    setActiveDescendantKey("");
  };

  const handleResultRowClick = (result: SearchSelectItem) => {
    handleSelection(result.key);
  };

  const getActiveDescendantKey = (ind: number) => {
    return mappedSearchResults[ind].key;
  };

  const handleSearchResultsKeyDown = (e: React.KeyboardEvent, result: SearchSelectItem) => {
    if (e.code === "Enter" || e.code === "Space") {
      e.preventDefault();
      handleSelection(result.key);
    } else if (e.code === "ArrowDown" || e.code === "ArrowUp") {
      if (e.code === "ArrowDown") {
        if (selectedIndexRef.current < itemRefs.current.length - 1) {
          selectedIndexRef.current++;
        } else if (selectedIndexRef.current === itemRefs.current.length - 1) {
          selectedIndexRef.current = 0;
        }
      } else {
        if (selectedIndexRef.current > 0) {
          selectedIndexRef.current--;
        } else if (selectedIndexRef.current === 0) {
          selectedIndexRef.current = itemRefs.current.length - 1;
        }
      }
      itemRefs.current[selectedIndexRef.current]?.current?.focus();

      setActiveDescendantKey(getActiveDescendantKey(+selectedIndexRef.current));
    } else if (e.code === "Escape") {
      handleClearOnClick();
      selectedIndexRef.current = -1;
      inputRef.current?.focus();
    } else if (e.code === "Tab") {
      if (e.shiftKey) {
        // backwards tab, focus the clear button
        closeIconRef.current?.focus();
      }
      selectedIndexRef.current = -1;
    }
  };

  const highlightText = (label: string, searchVal: string) => {
    const startOfFoundSubstring = label?.toLowerCase().indexOf(searchVal.toLowerCase());

    if (startOfFoundSubstring === -1) {
      return <p className="aboName">{label}</p>;
    }

    const lengthOfSearchVal = searchVal.length;
    const endOfFoundSubstring = startOfFoundSubstring + lengthOfSearchVal;

    return (
      <p className="aboName">
        {label?.slice(0, startOfFoundSubstring)}
        <span style={{ fontWeight: 700 }}>{label?.substring(startOfFoundSubstring, endOfFoundSubstring)}</span>
        {label?.slice(endOfFoundSubstring)}
      </p>
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== "") {
      setSelectedFullName("");
      const value = e.target.value;
      setInputText(value);
      if (e.target.value.length >= 1) {
        const results = searchCallback(value);
        setMappedSearchResults(results);
        itemRefs.current = results.map(() => React.createRef<HTMLDivElement>());
      } else {
        // input length is less than three
        setMappedSearchResults([]);
      }
    } else {
      handleClearOnClick();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === "Escape") {
      handleClearOnClick();
    } else if (e.code === "ArrowDown") {
      e.preventDefault();
      itemRefs.current[0]?.current?.focus();
      selectedIndexRef.current = 0;
      setActiveDescendantKey(getActiveDescendantKey(+selectedIndexRef.current));
    } else if (e.code === "ArrowUp") {
      e.preventDefault();
      itemRefs.current[itemRefs.current.length - 1]?.current?.focus();
      selectedIndexRef.current = itemRefs.current.length - 1;
      setActiveDescendantKey(getActiveDescendantKey(+selectedIndexRef.current));
    }
  };

  const handleClearOnClick = () => {
    setInputText("");
    setSelectedFullName("");
    setActiveDescendantKey("");
    setMappedSearchResults([]);
    clear();
    selectedIndexRef.current = -1;
    inputRef.current?.focus();
  };

  return (
    <div className="searchWrapper">
      <div className="searchSelectWrapper">
        <div className={`inputDiv ${disabled ? "inputDiv__disabled" : ""}`}>
          <span className={`searchIcon ${disabled ? "searchIcon--disabled" : ""}`}>
            <IconSearch height="1.5rem" width="1.5rem" />
          </span>
          <input
            className={`input ${disabled ? "input__disabled" : ""}`}
            placeholder={placeholderText}
            onChange={(e) => handleInputChange(e)}
            onKeyDown={(e) => {
              handleInputKeyDown(e);
            }}
            value={selectedFullName !== "" ? selectedFullName : inputText}
            aria-controls="results-list"
            aria-expanded={mappedSearchResults?.length > 0}
            role="combobox"
            ref={inputRef}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            aria-activedescendant={activeDescendantKey}
            aria-label={placeholderText}
          />
          {(inputText !== "" || selectedFullName !== "") && (
            <div
              onClick={handleClearOnClick}
              onKeyDown={(e) => {
                if (e.code === "Enter" || e.code === "Space") {
                  e.preventDefault();
                  handleClearOnClick();
                }
              }}
              aria-label={clearSearchAriaLabel}
              ref={closeIconRef}
              tabIndex={disabled ? -1 : 0}
              role="button"
              className="closeButton"
            >
              <IconClose height="0.984rem" width="0.656rem" useAlternateIcon />
            </div>
          )}
        </div>
      </div>
      {mappedSearchResults?.length > 0 && (
        <div
          role="listbox"
          tabIndex={-1}
          id="results-list"
          className="searchSelectResultsWrapper"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
            }
          }}
          key={inputText}
        >
          {mappedSearchResults.map((result: SearchSelectItem, index: number) => (
            <div
              className="resultItem"
              role="option"
              aria-selected={false}
              key={result.key}
              tabIndex={-1}
              onClick={() => handleResultRowClick(result)}
              onKeyDown={(e) => handleSearchResultsKeyDown(e, result)}
              ref={itemRefs.current[index]}
              aria-label={result.label}
            >
              {highlightText(result.label, inputText)}
              {result.sublabel && <p className="aboAff">{result.sublabel}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
